/**
 * Shared CSV Schema Constants
 *
 * These constants define the standard column names used in CSV transformations
 * for rebate invoices and other data import workflows.
 */

/**
 * Standard column names for rebate invoice CSV files
 */
export const REBATE_CSV_COLUMNS = {
  DATE: "Date",
  DIVISION_NAME: "Division Name",
  PRODUCT_GROUP_NAME: "Product Group Name",
  VALUE: "Value",
  SUPPLIER_NAME: "Supplier Name",
  CURRENCY: "Currency",
} as const;

/**
 * Array of required columns for rebate invoice CSV validation
 * Note: All columns listed must be present in the CSV header.
 * Product Group Name can have empty/null/"All Products" to mean all products.
 */
export const REQUIRED_REBATE_CSV_COLUMNS = [
  REBATE_CSV_COLUMNS.DATE,
  REBATE_CSV_COLUMNS.DIVISION_NAME,
  REBATE_CSV_COLUMNS.PRODUCT_GROUP_NAME, // Column is required, but value can be empty/"All Products"
  REBATE_CSV_COLUMNS.VALUE,
] as const;

/**
 * Type for rebate CSV column names
 */
export type RebateCsvColumn =
  (typeof REBATE_CSV_COLUMNS)[keyof typeof REBATE_CSV_COLUMNS];

/**
 * Summary info about a product group used by a rebate transaction record's line items.
 */
export interface RebateTransactionRecordProductGroupInfo {
  productGroupId: string;
  productGroupName: string;
  lineItemCount: number;
  totalValue: number;
  /** Whether this product group exists in the target version's rules */
  isCompatible: boolean;
}

/**
 * Line item detail returned in migration analysis
 */
export interface MigrationLineItem {
  id: string;
  date: string;
  divisionName: string;
  productGroupId: string | null;
  productGroupName: string | null; // null = "All Products"
  value: number;
  currency: string;
  isCompatible: boolean;
}

/**
 * Analysis result for CSV migration compatibility
 */
export interface CsvAnalysis {
  invoiceId: string;
  invoiceNumber: string;
  canMigrate: boolean;
  status: "compatible" | "compatible_with_notice" | "incompatible";
  lineItemCount: number;
  warnings: string[];
  blockers: string[];
  /** Product group summary for this rebate transaction record */
  productGroups?: RebateTransactionRecordProductGroupInfo[];
  /** Individual line items for the mapping UI */
  lineItems?: MigrationLineItem[];
}

/**
 * Per-rebate-transaction-record migration instruction with per-line-item product group mappings
 */
export interface RebateTransactionRecordMigrationInstruction {
  invoiceId: string;
  /** Per-line-item mappings. Only include items whose product group should change. */
  lineItemMappings: Array<{
    lineItemId: string;
    toProductGroupId: string | null; // null = "All Products"
  }>;
}

/**
 * Compatibility report for CSV migration between rebate agreement versions
 */
export interface MigrationCompatibilityReport {
  compatible: boolean;
  csvAnalysis: CsvAnalysis[];
  /** Product groups available in the target version for remapping dropdowns */
  targetProductGroups?: Array<{
    id: string;
    name: string;
    isCatchAll: boolean;
  }>;
}
